import { useCallback, useEffect, useState } from 'react'
import { Check, Copy, Globe } from 'lucide-react'

import { Button, ExplainDisabled } from '@/components/ui/button'
import { ErrorNote, Spinner } from '@/components/ui/misc'
import { api, ApiError } from '@/lib/api'
import { useAuth } from '@/features/auth/AuthProvider'

/**
 * Permanent public URL for one object, backed by its HCP ACL.
 *
 * Distinct from a share link: this grants anonymous read on the object itself,
 * so HCP serves the file directly and this application never sees the request.
 * That makes it fast and free, and also means downloads cannot be counted,
 * attributed or expired — which is exactly the trade-off spelled out below.
 */
export function PublicLinkPanel({ bucket, objectKey }: { bucket: string; objectKey: string }) {
  const { canEdit } = useAuth()
  const [isPublic, setIsPublic] = useState(false)
  const [url, setUrl] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)

  const load = useCallback(() => {
    setLoading(true)
    api
      .objectPublic(bucket, objectKey)
      .then((res) => {
        setIsPublic(res.isPublic)
        setUrl(res.url ?? res.potentialUrl)
      })
      .catch(() => {
        // Namespaces without ACLs enabled reject the query outright. Treating
        // that as "not public" is accurate, and the toggle will surface the
        // real error if the user tries to change it.
        setIsPublic(false)
      })
      .finally(() => setLoading(false))
  }, [bucket, objectKey])

  useEffect(load, [load])

  async function toggle(next: boolean) {
    setBusy(true)
    setError(null)
    try {
      const res = await api.setObjectPublic(bucket, objectKey, next)
      setIsPublic(res.isPublic)
      if (res.url) setUrl(res.url)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not change public access')
    } finally {
      setBusy(false)
    }
  }

  async function copy() {
    if (!url) return
    try {
      await navigator.clipboard.writeText(url)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 2000)
    } catch {
      // Clipboard may be blocked; the field is selectable as a fallback.
    }
  }

  if (loading) {
    return (
      <div className="flex justify-center py-4">
        <Spinner />
      </div>
    )
  }

  return (
    <div className="space-y-3 rounded-md border border-border bg-surface p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="space-y-0.5">
          <p className="flex items-center gap-1.5 text-sm font-medium">
            <Globe className={isPublic ? 'h-4 w-4 text-warning' : 'h-4 w-4 text-muted-foreground'} />
            Public link
          </p>
          <p className="text-xs text-muted-foreground">
            {isPublic
              ? 'Anyone with this URL can download the file without signing in.'
              : 'This file is private. Only signed-in users can download it.'}
          </p>
        </div>

        <ExplainDisabled reason={canEdit ? null : 'Changing public access requires the Editor role.'}>
          <Button
            size="sm"
            variant={isPublic ? 'danger' : 'secondary'}
            disabled={!canEdit}
            loading={busy}
            onClick={() => void toggle(!isPublic)}
          >
            {isPublic ? 'Make private' : 'Make public'}
          </Button>
        </ExplainDisabled>
      </div>

      {error && <ErrorNote>{error}</ErrorNote>}

      {isPublic && url && (
        <>
          <div className="flex items-center gap-1.5 rounded-md border border-border bg-background px-2 py-1.5">
            <input
              readOnly
              value={url}
              onFocus={(e) => e.currentTarget.select()}
              className="min-w-0 flex-1 bg-transparent font-mono text-[11px] outline-none"
              aria-label="Public link"
            />
            <button
              onClick={() => void copy()}
              className="shrink-0 rounded p-1 hover:bg-muted"
              aria-label="Copy public link"
              title="Copy public link"
            >
              {copied ? <Check className="h-3.5 w-3.5 text-success" /> : <Copy className="h-3.5 w-3.5" />}
            </button>
          </div>

          <p className="text-xs text-muted-foreground">
            Served directly by HCP, so downloads through this link are{' '}
            <strong>not recorded</strong> in the audit log and cannot be expired. Use a share link
            instead if you need either.
          </p>
        </>
      )}
    </div>
  )
}
